/**
 * Utilities for the Last Session Snapshot homepage hero:
 * - pickPrimaryLadder  — chooses Mixed vs Levels as the featured discipline
 * - computeMaxWinStreakInSession — highest consecutive-win streak achieved during a session
 */

const LADDER_LABELS = {
  mixed: 'Mixed',
  levels_male: "Men's Doubles",
  levels_female: "Women's Doubles",
};

/** Short labels for the hero ladder toggle. */
export const LADDER_TOGGLE_LABELS = {
  mixed: 'Mixed',
  levels_male: "Men's",
  levels_female: "Women's",
};

export function getLadderLabel(key) {
  return LADDER_LABELS[key] || key;
}

export function getLadderToggleLabel(key) {
  return LADDER_TOGGLE_LABELS[key] || getLadderLabel(key);
}

/**
 * Returns the levels ladder key for a viewer based on their gender.
 * @param {'male'|'female'|null|undefined} viewerGender
 * @returns {'levels_male'|'levels_female'|null}
 */
export function levelsLadderForGender(viewerGender) {
  if (viewerGender === 'male') return 'levels_male';
  if (viewerGender === 'female') return 'levels_female';
  return null;
}

/**
 * Summarises W/L, win rate, and max streak for an ordered list of game results.
 *
 * @param {string[]} orderedResults  'win'|'loss'|'draw', oldest → newest
 */
export function summarizeLadderGameResults(orderedResults) {
  const results = orderedResults || [];
  const wins = results.filter((r) => r === 'win').length;
  const losses = results.filter((r) => r === 'loss').length;
  const gamesPlayed = results.length;
  return {
    wins,
    losses,
    gamesPlayed,
    winRatePct: gamesPlayed > 0 ? Math.round((wins / gamesPlayed) * 100) : null,
    maxWinStreak: computeMaxWinStreakInSession(results),
  };
}

/**
 * Last N session game results as a W/L string (oldest → newest), matching live SessionRanking.
 *
 * @param {Array<{ result?: string, created_at?: string }>} games
 * @param {number} [maxGames=5]
 */
export function buildRecentFormString(games, maxGames = 5) {
  if (!games?.length) return '';
  const sorted = [...games].sort(
    (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
  );
  return sorted
    .slice(-maxGames)
    .map((g) => (g.result === 'win' ? 'W' : 'L'))
    .join('');
}

/**
 * Builds full ladder entries for Mixed and Levels (when played) plus the default featured key.
 * `rank` and `rankDelta` start as the full-club snapshot values. The post-session
 * hero replaces them via enrichSessionLadderSnapshot so they match the Active leaderboard.
 *
 * @param {{
 *   mixedStats: ReturnType<typeof summarizeLadderGameResults>,
 *   levelsStats: ReturnType<typeof summarizeLadderGameResults>,
 *   mixedEloDelta: number,
 *   levelsEloDelta: number,
 *   snapshotRecap: Array,
 *   viewerGender: 'male'|'female'|null|undefined,
 * }} opts
 */
export function buildSessionLadderSnapshot({
  mixedStats,
  levelsStats,
  mixedEloDelta,
  levelsEloDelta,
  snapshotRecap,
  viewerGender,
}) {
  const levelsKey = levelsLadderForGender(viewerGender);
  const mixedGames = mixedStats?.gamesPlayed ?? 0;
  const levelsGames = levelsStats?.gamesPlayed ?? 0;

  const findSnapshot = (ladderType) =>
    (snapshotRecap || []).find((e) => e.ladder_type === ladderType) || null;

  const buildEntry = (key, fallbackDelta, stats) => {
    const snap = findSnapshot(key);
    return {
      key,
      label: getLadderLabel(key),
      toggleLabel: getLadderToggleLabel(key),
      gamesPlayed: stats.gamesPlayed,
      wins: stats.wins,
      losses: stats.losses,
      winRatePct: stats.winRatePct,
      maxWinStreak: stats.maxWinStreak,
      rank: snap?.rank ?? null,
      rankDelta: snap?.rank_delta ?? null,
      eloRating: snap?.elo_rating ?? null,
      eloDelta: snap?.session_elo_delta ?? fallbackDelta ?? 0,
    };
  };

  const emptyStats = summarizeLadderGameResults([]);

  let mixed =
    mixedGames > 0 ? buildEntry('mixed', mixedEloDelta, mixedStats) : null;
  let levels =
    levelsGames > 0 && levelsKey
      ? buildEntry(levelsKey, levelsEloDelta, levelsStats)
      : null;

  // Snapshot-only levels entry (club ladder rank without levels games in session)
  if (!levels && levelsKey) {
    const snap = findSnapshot(levelsKey);
    if (snap) {
      const snapGames = snap.session_games ?? 0;
      levels = {
        key: levelsKey,
        label: getLadderLabel(levelsKey),
        toggleLabel: getLadderToggleLabel(levelsKey),
        gamesPlayed: snapGames,
        wins: snap.session_wins ?? 0,
        losses: snap.session_losses ?? 0,
        winRatePct: snapGames > 0 ? Math.round(((snap.session_wins ?? 0) / snapGames) * 100) : null,
        maxWinStreak: 0,
        rank: snap.rank ?? null,
        rankDelta: snap.rank_delta ?? null,
        eloRating: snap.elo_rating ?? null,
        eloDelta: snap.session_elo_delta ?? levelsEloDelta ?? 0,
      };
    }
  }

  if (!mixed && !levels) {
    mixed = buildEntry('mixed', mixedEloDelta || 0, mixedStats?.gamesPlayed ? mixedStats : emptyStats);
  }

  let defaultLadderKey = mixed?.key ?? levels?.key ?? 'mixed';

  if (mixed && levels) {
    if (levels.gamesPlayed > mixed.gamesPlayed) {
      defaultLadderKey = levels.key;
    } else if (
      levels.gamesPlayed === mixed.gamesPlayed &&
      Math.abs(levels.eloDelta) > Math.abs(mixed.eloDelta)
    ) {
      defaultLadderKey = levels.key;
    } else {
      defaultLadderKey = 'mixed';
    }
  } else if (levels && !mixed) {
    defaultLadderKey = levels.key;
  }

  const mixedSnap = findSnapshot('mixed');
  const levelsSnap = levelsKey ? findSnapshot(levelsKey) : null;
  const canToggleFromGames = Boolean(mixed && levels && mixed.gamesPlayed > 0 && levels.gamesPlayed > 0);
  const canToggleFromSnapshot = Boolean(mixedSnap && levelsSnap);

  return {
    mixed,
    levels,
    defaultLadderKey,
    canToggleLadder: canToggleFromGames || canToggleFromSnapshot,
  };
}

/**
 * Picks the primary ladder to feature in the Last Session Snapshot hero.
 *
 * @deprecated Prefer {@link buildSessionLadderSnapshot} for toggle support.
 */
export function pickPrimaryLadder(opts) {
  const mixedStats = opts.mixedStats ?? summarizeLadderGameResults([]);
  const levelsStats = opts.levelsStats ?? summarizeLadderGameResults([]);
  const { mixed, levels, defaultLadderKey } = buildSessionLadderSnapshot({
    mixedStats,
    levelsStats,
    mixedEloDelta: opts.mixedEloDelta,
    levelsEloDelta: opts.levelsEloDelta,
    snapshotRecap: opts.snapshotRecap,
    viewerGender: opts.viewerGender,
  });
  const primaryLadder =
    defaultLadderKey === levels?.key ? levels : mixed ?? levels;

  let secondaryLadder = null;
  if (mixed && levels && mixed.gamesPlayed > 0 && levels.gamesPlayed > 0) {
    const secondary = primaryLadder.key === 'mixed' ? levels : mixed;
    secondaryLadder = { label: secondary.label, eloDelta: secondary.eloDelta };
  }

  return { primaryLadder, secondaryLadder };
}

/**
 * Computes the highest consecutive-win streak achieved at any point during a session.
 * A draw or loss resets the running count; the max observed value is returned.
 *
 * @param {string[]} orderedResults  'win'|'loss'|'draw', sorted oldest → newest
 * @returns {number}
 */
export function computeMaxWinStreakInSession(orderedResults) {
  if (!orderedResults || orderedResults.length === 0) return 0;
  let max = 0;
  let current = 0;
  for (const result of orderedResults) {
    if (result === 'win') {
      current++;
      if (current > max) max = current;
    } else {
      current = 0;
    }
  }
  return max;
}
